import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import {
  Camera,
  Save,
  Loader2,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  FileText,
  CheckCircle,
  X,
  Upload,
  Trash2,
  Plus,
  Tag,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { authService } from '../lib/auth';
import { getUserProfile, updateUserProfile, uploadProfilePhoto, type UserProfile } from '../lib/user-profile-api';
import { motion } from 'motion/react';
import { API_BASE, getAuthHeaders, apiFetch } from '../lib/api-fetch';

export function AgentProfileEditor() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // SMTP configuration state
  const [smtpForm, setSmtpForm] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: false,
    fromName: '',
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);

  // Editable fields
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    phone: '',
    bio: '',
    region: '',
    rating: '',
    propertiesSold: '',
    yearsExperience: '',
  });

  // Tag arrays
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [areasServed, setAreasServed] = useState<string[]>([]);
  const [specializationInput, setSpecializationInput] = useState('');
  const [areasInput, setAreasInput] = useState('');

  // Photo preview
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
    loadSmtpConfig();
  }, []);

  const loadSmtpConfig = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/agent-email/configure`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        const config = data.data || {};
        setSmtpConfigured(config.configured === true);
        if (config.configured) {
          setSmtpForm(prev => ({
            ...prev,
            smtpHost: config.smtpHost || '',
            smtpPort: String(config.smtpPort || '587'),
            smtpUser: config.smtpUser || '',
            smtpSecure: config.smtpSecure === true,
            fromName: config.fromName || '',
          }));
        }
      }
    } catch {
      // Silently ignore — SMTP config is optional
    }
  };

  const handleSendTestEmail = async () => {
    if (!smtpConfigured) {
      toast.error('Save your SMTP configuration first before sending a test email');
      return;
    }
    const toAddress = smtpForm.smtpUser.trim();
    if (!toAddress) {
      toast.error('No email address to send test to — fill in the Email Address field');
      return;
    }
    setIsSendingTestEmail(true);
    try {
      const response = await apiFetch(`${API_BASE}/agent-email/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: toAddress,
          toName: smtpForm.fromName.trim() || toAddress,
          subject: 'Something Property — SMTP Test Email',
          body: `Hi,\n\nThis is a test email from your Something Property CRM to confirm your email configuration is working correctly.\n\nIf you received this, your SMTP settings are set up successfully.\n\nSomething Property CRM`,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error?.message || err.message || 'Failed to send test email');
      }
      toast.success(`Test email sent to ${toAddress}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to send test email — check your SMTP credentials');
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const handleSaveSmtp = async () => {
    if (!smtpForm.smtpHost.trim() || !smtpForm.smtpUser.trim() || !smtpForm.smtpPassword.trim()) {
      toast.error('SMTP host, user, and password are required');
      return;
    }
    setIsSavingSmtp(true);
    try {
      const response = await apiFetch(`${API_BASE}/agent-email/configure`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smtpHost: smtpForm.smtpHost.trim(),
          smtpPort: parseInt(smtpForm.smtpPort) || 587,
          smtpUser: smtpForm.smtpUser.trim(),
          smtpPassword: smtpForm.smtpPassword,
          smtpSecure: smtpForm.smtpSecure,
          fromName: smtpForm.fromName.trim() || smtpForm.smtpUser.trim(),
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save SMTP config');
      }
      setSmtpConfigured(true);
      setSmtpForm(prev => ({ ...prev, smtpPassword: '' }));
      toast.success('Email configuration saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save email configuration');
    } finally {
      setIsSavingSmtp(false);
    }
  };

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        toast.error('You must be logged in to view your profile');
        setIsLoading(false);
        return;
      }

      const userProfile = await getUserProfile(currentUser.id);
      if (userProfile) {
        setProfile(userProfile);
        setFormData({
          name: userProfile.name || '',
          lastName: (userProfile as any).lastName || '',
          phone: userProfile.phone || '',
          bio: userProfile.bio || '',
          region: userProfile.region || '',
          rating: userProfile.rating != null ? String(userProfile.rating) : '',
          propertiesSold: userProfile.propertiesSold != null ? String(userProfile.propertiesSold) : '',
          yearsExperience: userProfile.yearsExperience != null ? String(userProfile.yearsExperience) : '',
        });
        setSpecializations((userProfile as any).specializations || []);
        setAreasServed((userProfile as any).areasServed || []);
        setPhotoPreview(userProfile.avatar || null);
      } else {
        // If no profile yet, populate from auth
        setFormData({
          name: currentUser.name || '',
          lastName: '',
          phone: '',
          bio: '',
          region: '',
        });
      }
    } catch (error) {
      logger.error('Failed to load profile', error as Error, undefined, 'AgentProfileEditor');
      toast.error('Failed to load your profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload the photo
    setIsUploadingPhoto(true);
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        toast.error('You must be logged in to upload a photo');
        return;
      }

      const result = await uploadProfilePhoto(currentUser.id, file);
      setPhotoPreview(result.url);
      
      // Update local profile state
      if (profile) {
        setProfile({ ...profile, avatar: result.url });
      }
      
      toast.success('Profile photo uploaded successfully');
    } catch (error) {
      logger.error('Failed to upload photo', error as Error, undefined, 'AgentProfileEditor');
      toast.error('Failed to upload photo. Please try again.');
      // Revert preview on failure
      setPhotoPreview(profile?.avatar || null);
    } finally {
      setIsUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) return;

    try {
      await updateUserProfile(currentUser.id, { avatar: null });
      setPhotoPreview(null);
      if (profile) {
        setProfile({ ...profile, avatar: null as any });
      }
      toast.success('Profile photo removed');
    } catch (error) {
      logger.error('Failed to remove photo', error as Error, undefined, 'AgentProfileEditor');
      toast.error('Failed to remove photo');
    }
  };

  const handleSaveProfile = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      toast.error('You must be logged in to update your profile');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('First name is required');
      return;
    }

    setIsSaving(true);
    try {
      const updates: Record<string, any> = {
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        bio: formData.bio.trim(),
        region: formData.region.trim(),
        specializations,
        areasServed,
      };

      if (formData.rating !== '') {
        const r = parseFloat(formData.rating);
        if (!isNaN(r) && r >= 0 && r <= 5) updates.rating = r;
      }
      if (formData.propertiesSold !== '') {
        const p = parseInt(formData.propertiesSold, 10);
        if (!isNaN(p) && p >= 0) updates.propertiesSold = p;
      }
      if (formData.yearsExperience !== '') {
        const y = parseInt(formData.yearsExperience, 10);
        if (!isNaN(y) && y >= 0) updates.yearsExperience = y;
      }

      // Build full display name
      if (formData.lastName.trim()) {
        updates.displayName = `${formData.name.trim()} ${formData.lastName.trim()}`;
      }

      await updateUserProfile(currentUser.id, updates);
      // Lambda returns { success: true } — no user object echoed back.
      // Update local state from the values we just saved.
      setProfile(prev => prev ? {
        ...prev,
        name: updates.name,
        phone: updates.phone,
        bio: updates.bio,
        ...(updates.lastName && { lastName: updates.lastName }),
        ...(updates.region && { region: updates.region }),
      } : prev);
      setHasChanges(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      logger.error('Failed to save profile', error as Error, undefined, 'AgentProfileEditor');
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = () => {
    const first = formData.name?.[0] || '';
    const last = formData.lastName?.[0] || '';
    return (first + last).toUpperCase() || 'A';
  };

  const addTag = (
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
  ) => {
    const trimmed = value.trim();
    if (!trimmed || list.includes(trimmed)) return;
    setList([...list, trimmed]);
    setInput('');
    setHasChanges(true);
  };

  const removeTag = (index: number, list: string[], setList: (v: string[]) => void) => {
    setList(list.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleTagKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(value, list, setList, setInput);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#FDB022] mx-auto" />
          <p className="text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card className="border-[#FDB022]/20 bg-gradient-to-r from-[#FDB022]/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Photo Upload Area */}
              <div className="relative group">
                <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-[#FDB022]/30 shadow-lg shadow-[#FDB022]/10">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
                      <span className="text-3xl font-bold text-[#FDB022]">
                        {getInitials()}
                      </span>
                    </div>
                  )}

                  {/* Upload overlay */}
                  {isUploadingPhoto ? (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-[#FDB022]" />
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      data-testid="agent-profile-photo-upload-overlay"
                    >
                      <Camera className="h-8 w-8 text-white" />
                    </button>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />

                {/* Photo action buttons */}
                <div className="flex items-center justify-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-[#FDB022]/30 text-[#FDB022] hover:bg-[#FDB022]/10"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingPhoto}
                    data-testid="agent-profile-photo-upload"
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    {photoPreview ? 'Change' : 'Upload'}
                  </Button>
                  {photoPreview && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
                      onClick={handleRemovePhoto}
                      data-testid="agent-profile-photo-remove"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>

              {/* Profile Summary */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold">
                  {formData.name} {formData.lastName}
                </h2>
                <p className="text-[#FDB022] font-medium mt-1">
                  {profile?.role || 'Agent'} 
                  {formData.region ? ` - ${formData.region}` : ''}
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  {profile?.email}
                </p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3">
                  <Badge variant="outline" className="border-[#FDB022]/30 text-[#FDB022]">
                    <Briefcase className="h-3 w-3 mr-1" />
                    {profile?.propertiesManaged || 0} Properties
                  </Badge>
                  <Badge variant="outline" className="border-green-500/30 text-green-400">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {profile?.salesCompleted || 0} Sales
                  </Badge>
                  <Badge variant="outline" className="border-blue-500/30 text-blue-400">
                    <User className="h-3 w-3 mr-1" />
                    {profile?.clientsManaged || 0} Clients
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Upload a professional headshot to appear on your agent profile page and property listings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Profile Edit Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Profile Information</CardTitle>
            <CardDescription>
              Update your personal details. These will appear on your public agent profile.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  First Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  name="name"
                  placeholder="John"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Last Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  placeholder="Smith"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email
                </Label>
                <Input
                  value={profile?.email || ''}
                  disabled
                  className="bg-[#1a1a1a] border-white/10 opacity-60 cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed here. Contact admin.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number
                </Label>
                <Input
                  placeholder="+27821234567"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Region
              </Label>
              <Input
                placeholder="e.g. Johannesburg, Cape Town"
                value={formData.region}
                onChange={(e) => handleFormChange('region', e.target.value)}
                className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Professional Bio
              </Label>
              <Textarea
                placeholder="Tell clients about your experience, specializations, and what makes you the right agent for them..."
                value={formData.bio}
                onChange={(e) => handleFormChange('bio', e.target.value)}
                rows={4}
                className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022] resize-none"
              />
              <p className="text-xs text-muted-foreground">
                {formData.bio.length}/500 characters
              </p>
            </div>

            <Separator className="my-4" />

            {/* Professional Stats */}
            <div>
              <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wide">Professional Stats (self-reported)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Rating (0.0 – 5.0)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    placeholder="e.g. 4.8"
                    value={formData.rating}
                    onChange={(e) => handleFormChange('rating', e.target.value)}
                    className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                    data-testid="agent-profile-rating"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Properties Sold</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 24"
                    value={formData.propertiesSold}
                    onChange={(e) => handleFormChange('propertiesSold', e.target.value)}
                    className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                    data-testid="agent-profile-properties-sold"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Years Experience</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 8"
                    value={formData.yearsExperience}
                    onChange={(e) => handleFormChange('yearsExperience', e.target.value)}
                    className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                    data-testid="agent-profile-years-experience"
                  />
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            {/* Specializations */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                Specialities
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {specializations.map((tag, idx) => (
                  <span
                    key={idx}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[#FDB022]/10 border border-[#FDB022]/30 text-[#FDB022]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(idx, specializations, setSpecializations)}
                      className="hover:text-red-400 transition-colors"
                      data-testid={`remove-specialization-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. Luxury Properties, First-time Buyers (Enter to add)"
                  value={specializationInput}
                  onChange={(e) => setSpecializationInput(e.target.value)}
                  onKeyDown={(e) => handleTagKeyDown(e, specializationInput, specializations, setSpecializations, setSpecializationInput)}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="specialization-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[#FDB022]/30 text-[#FDB022] hover:bg-[#FDB022]/10 shrink-0"
                  onClick={() => addTag(specializationInput, specializations, setSpecializations, setSpecializationInput)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Areas Served */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Areas Served
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {areasServed.map((tag, idx) => (
                  <span
                    key={idx}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-500/10 border border-blue-500/30 text-blue-400"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(idx, areasServed, setAreasServed)}
                      className="hover:text-red-400 transition-colors"
                      data-testid={`remove-area-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. Sandton, Fourways, Midrand (Enter to add)"
                  value={areasInput}
                  onChange={(e) => setAreasInput(e.target.value)}
                  onKeyDown={(e) => handleTagKeyDown(e, areasInput, areasServed, setAreasServed, setAreasInput)}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="areas-served-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 shrink-0"
                  onClick={() => addTag(areasInput, areasServed, setAreasServed, setAreasInput)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {hasChanges ? (
                  <span className="text-[#FDB022]">You have unsaved changes</span>
                ) : (
                  <span>All changes saved</span>
                )}
              </div>
              <Button
                onClick={handleSaveProfile}
                disabled={isSaving || !hasChanges}
                className="bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-medium"
                data-testid="agent-profile-save"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Email (SMTP) Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-[#FDB022]" />
                  Email Configuration
                </CardTitle>
                <CardDescription>
                  Connect your cPanel email to send emails to leads and clients directly from the CRM.
                </CardDescription>
              </div>
              {smtpConfigured && (
                <span className="text-xs text-green-400 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Configured
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SMTP Host</Label>
                <Input
                  placeholder="mail.somethingproperty.co.za"
                  value={smtpForm.smtpHost}
                  onChange={e => setSmtpForm(prev => ({ ...prev, smtpHost: e.target.value }))}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="smtp-host"
                />
              </div>
              <div className="space-y-2">
                <Label>SMTP Port</Label>
                <Input
                  type="number"
                  placeholder="587"
                  value={smtpForm.smtpPort}
                  onChange={e => setSmtpForm(prev => ({ ...prev, smtpPort: e.target.value }))}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="smtp-port"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address (SMTP Username)</Label>
                <Input
                  type="email"
                  placeholder="yourname@somethingproperty.co.za"
                  value={smtpForm.smtpUser}
                  onChange={e => setSmtpForm(prev => ({ ...prev, smtpUser: e.target.value }))}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="smtp-user"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder={smtpConfigured ? '••••••••' : 'Email password'}
                  value={smtpForm.smtpPassword}
                  onChange={e => setSmtpForm(prev => ({ ...prev, smtpPassword: e.target.value }))}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="smtp-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name (From)</Label>
                <Input
                  placeholder="Your Name"
                  value={smtpForm.fromName}
                  onChange={e => setSmtpForm(prev => ({ ...prev, fromName: e.target.value }))}
                  className="bg-[#1a1a1a] border-white/10 focus:border-[#FDB022]"
                  data-testid="smtp-from-name"
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="smtp-secure"
                  checked={smtpForm.smtpSecure}
                  onChange={e => setSmtpForm(prev => ({ ...prev, smtpSecure: e.target.checked }))}
                  className="w-4 h-4"
                  data-testid="smtp-secure"
                />
                <Label htmlFor="smtp-secure">Use SSL/TLS (port 465)</Label>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={handleSaveSmtp}
                disabled={isSavingSmtp}
                className="bg-[#FDB022] text-black hover:bg-[#FDB022]/90"
                data-testid="smtp-save-button"
              >
                {isSavingSmtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {smtpConfigured ? 'Update Email Config' : 'Save Email Config'}
              </Button>
              {smtpConfigured && (
                <Button
                  variant="outline"
                  onClick={handleSendTestEmail}
                  disabled={isSendingTestEmail}
                  data-testid="smtp-test-button"
                >
                  {isSendingTestEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Send Test Email
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Profile Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <Card className="border-blue-500/20 bg-blue-500/5">
          <CardContent className="p-4">
            <h4 className="font-medium text-blue-400 mb-2">Profile Tips</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                Use a professional headshot with good lighting and a clean background
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                Write a compelling bio that highlights your experience and local expertise
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                Keep your phone number current so clients can reach you directly
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                Your profile appears on all your property listings and in the agent directory
              </li>
            </ul>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
